import { useEffect, useState } from 'react';
import {
  fetchHolidaysSetForWorker, subdivisionForCommunity, subdivisionForProvince,
  groupHolidaysByDate, holidayScopeRank,
} from '../lib/spainHolidays';

const resolveSubdivision = (worker) =>
  subdivisionForCommunity(worker?.autonomousCommunity) ||
  subdivisionForProvince(worker?.province) ||
  null;

// useHolidays(user|null, year)
//   Devuelve { holidays: Set<ISO>, byDate: Map<ISO, item>, allByDate: Map<ISO, item[]>, list, loading, community, subdivision }
//   - holidays: backwards compatible, Set de fechas ISO.
//   - byDate: lookup rápido día → { date, name, scope, province, locality }
//     scope ∈ 'national' | 'regional' | 'local' | 'company' (para colorear en
//     el calendario). UN festivo por día: el oficial manda sobre el de empresa.
//   - allByDate: TODOS los festivos de cada día (para mostrar nombres; los de
//     empresa llevan la etiqueta "Festivo de empresa").
//   - `year` puede ser un número o un array de años. Si es array, devuelve
//     el set/byDate/list combinados de todos los años (útil para el cómputo
//     de la bolsa de horas, ver balanceHolidayYears en lib/hourBalance).
//   - `refreshKey` (opcional, igual que la app): al cambiarlo se vuelve a
//     pedir. Para saltarse la caché, llamar antes a invalidateHolidayCache().
export const useHolidays = (user, year, refreshKey = 0) => {
  const province = typeof user === 'string' ? user : user?.province;
  const community = typeof user === 'string' ? null : user?.autonomousCommunity;
  // La localidad es necesaria para filtrar los festivos LOCALES del backend
  // (ver fetchHolidaysSetForWorker en lib/spainHolidays).
  const locality = typeof user === 'string' ? null : user?.locality;
  // La empresa es necesaria para los festivos DE EMPRESA (solo aplican a
  // trabajadores de esa empresa). Sin comunidad ni provincia pero con
  // empresa, se cargan igualmente sus festivos de empresa.
  const companyId = typeof user === 'string' ? null : (user?.companyId ?? null);
  const years = Array.isArray(year) ? year.filter((y) => Number.isFinite(y)) : (year ? [year] : []);
  const yearsKey = years.join(',');
  const initLoading = !!(community || province || companyId) && years.length > 0;
  const [state, setState] = useState({
    holidays: new Set(), byDate: new Map(), allByDate: new Map(), list: [], loading: initLoading,
    community: null, subdivision: null,
  });

  useEffect(() => {
    let cancelled = false;
    if (years.length === 0 || (!community && !province && !companyId)) {
      setState({ holidays: new Set(), byDate: new Map(), allByDate: new Map(), list: [], loading: false, community: null, subdivision: null });
      return;
    }
    (async () => {
      setState((s) => ({ ...s, loading: true }));
      const fakeWorker = { autonomousCommunity: community, province, locality, companyId };
      // fetchHolidaysSetForWorker ya carga todos los años en paralelo y
      // combina set + list (la caché interna deduplica peticiones).
      const { set: combinedSet, list: combinedList, subdivisionCode: lastSub } =
        await fetchHolidaysSetForWorker(fakeWorker, years);
      if (cancelled) return;
      const byDate = new Map();
      for (const h of combinedList) {
        if (!h.date) continue;
        // Si dos festivos coinciden el mismo día, prevalece el más restrictivo
        // visualmente: local > regional > national > company.
        const prev = byDate.get(h.date);
        if (!prev || holidayScopeRank(h.scope) > holidayScopeRank(prev.scope)) byDate.set(h.date, h);
      }
      setState({
        holidays: combinedSet,
        byDate,
        allByDate: groupHolidaysByDate(combinedList),
        list: combinedList,
        loading: false,
        community: community || null,
        subdivision: lastSub,
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [community, province, locality, companyId, yearsKey, refreshKey]);

  return state;
};

// useMultiHolidays(workers[{ id, autonomousCommunity, province, locality, community, companyId }], year)
//   Devuelve { byWorker: Map<workerId, Set<ISO>>, byWorkerDetail: Map<workerId, Map<ISO, item[]>>, loading }
//   Pasa por fetchHolidaysSetForWorker (mismo filtro que useHolidays: locales
//   por provincia/localidad y festivos de empresa solo para su empresa).
export const useMultiHolidays = (workers = [], year) => {
  const dedup = (workers || []).filter((w) => w && (w.autonomousCommunity || w.community || w.province || w.companyId));
  const depKey = `${year}|${dedup.map((w) =>
    `${w.id}:${resolveSubdivision({ autonomousCommunity: w.autonomousCommunity || w.community, province: w.province }) || ''}:${w.province || ''}:${w.locality || ''}:${w.companyId || ''}`
  ).join(',')}`;
  const [state, setState] = useState({ byWorker: new Map(), byWorkerDetail: new Map(), loading: false });

  useEffect(() => {
    let cancelled = false;
    if (dedup.length === 0 || !year) {
      setState({ byWorker: new Map(), byWorkerDetail: new Map(), loading: false });
      return;
    }
    (async () => {
      setState((s) => ({ ...s, loading: true }));
      // Fetch en paralelo; las cachés internas deduplican subdivisiones y
      // empresas repetidas.
      const results = await Promise.all(dedup.map(async (w) => {
        const { set, list } = await fetchHolidaysSetForWorker({
          autonomousCommunity: w.autonomousCommunity || w.community || null,
          province: w.province || null,
          locality: w.locality || null,
          companyId: w.companyId ?? null,
        }, year);
        return [w.id, set, groupHolidaysByDate(list)];
      }));
      if (cancelled) return;
      const map = new Map();
      const detail = new Map();
      for (const [id, set, byDate] of results) {
        map.set(id, set);
        detail.set(id, byDate);
      }
      setState({ byWorker: map, byWorkerDetail: detail, loading: false });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey]);

  return state;
};
